import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserProducts, UserRoleMappingUpdate } from "auth";
import { useDebounce } from "use-debounce";

import { GetUsersPayload, GetUsersQueryParams } from "lib/dto";
import { useCurrentCompany } from "ui/context/auth";
import { useRestApi } from "ui/context/restApi";

import { queryKeys } from "./queryKeys";

export const useSelfQuery = (loggedIn: boolean) => {
  const { getSelf } = useRestApi();

  return useQuery({
    queryKey: [queryKeys.SELF],
    queryFn: getSelf,
    enabled: loggedIn,
    staleTime: Infinity,
  });
};

export const useUsersQuery = (queryParams: GetUsersPayload & GetUsersQueryParams) => {
  const { getUsers } = useRestApi().user;
  const currentCompany = useCurrentCompany();
  const { email, teamIds, roleIds, ...params } = queryParams;
  const payload: GetUsersPayload = {
    email,
    teamIds,
    roleIds,
  };
  return useQuery({
    queryKey: [
      queryKeys.USER_LIST,
      useDebounce(payload.email, 500),
      payload.teamIds,
      queryParams.page,
      queryParams.size,
      queryParams.sort,
      currentCompany.id,
    ],
    queryFn: () => getUsers(currentCompany.id, payload, params),
    staleTime: Infinity,
  });
};

export const useUserDetailsQuery = (id: string) => {
  const { getUserDetails } = useRestApi().user;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.USER_DETAILS, id, currentCompany.id],
    queryFn: () => getUserDetails(id, currentCompany.id),
    staleTime: Infinity,
    placeholderData: keepPreviousData,
  });
};

export const useUserProductsQuery = (userId: string) => {
  const { getUserProducts } = useRestApi().user;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.USER_PRODUCTS, userId, currentCompany.id],
    queryFn: () => getUserProducts(currentCompany.id, userId),
    staleTime: Infinity,
    placeholderData: keepPreviousData,
  });
};

export const useUpdateUserRolesMutation = () => {
  const { updateUserRoles } = useRestApi().user;
  const queryClient = useQueryClient();
  const currentCompany = useCurrentCompany();

  return useMutation({
    mutationFn: (args: { userId: string; roleMapping: UserRoleMappingUpdate["roles"] }) => {
      const userRoleMappingUpdate: UserRoleMappingUpdate = {
        uuid: args.userId,
        roles: args.roleMapping,
      };
      return updateUserRoles(currentCompany.id, args.userId, userRoleMappingUpdate);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USER_LIST],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.USER_DETAILS, variables.userId],
        refetchType: "active",
      });
    },
  });
};

export const useUpdateUserProductsMutation = () => {
  const { updateUserProducts } = useRestApi().user;
  const currentCompany = useCurrentCompany();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: { userId: string; products: UserProducts["products"] }) =>
      updateUserProducts(currentCompany.id, args.userId, { products: args.products }),
    onSuccess: (_data, variables) => {
      queryClient.setQueryData(
        [queryKeys.USER_PRODUCTS, variables.userId, currentCompany.id],
        _data,
      );
    },
  });
};
